import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  TextField,
  Typography,
  IconButton,
  FormControl,
  FormLabel,
  FormControlLabel,
  RadioGroup,
  Radio,
  Button,
  CircularProgress,
  Alert,
  Chip,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemText,
  Divider,
  Snackbar,
  Slide,
  Autocomplete,
  Switch,
  Select,
  MenuItem,
  InputLabel,
  Menu,
} from '@mui/material';
import {
  Domain as DomainIcon,
  Refresh as RefreshIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
  ContentCopy as ContentCopyIcon,
  ChevronRight as ChevronRightIcon,
  ChevronLeft as ChevronLeftIcon,
  Close as CloseIcon,
  CloudUpload as UploadIcon,
  Collections as LibraryIcon,
} from '@mui/icons-material';
import { SiteEditState } from '../../../../types/visualEditor';
import { ImageMetadata, ImageService } from '../../../../services/imageService';
import { ImageLibraryModal } from '../../ImageLibraryModal';
import { DomainService } from '../../../../services/domainService';
import { DomainConfig, User } from '../../../../types';
import { useSites } from '../../../../hooks/useSites';
import { UserService } from '../../../../services/userService';
import { RightDrawer, ConfirmDialog, ImageUploadField } from '../../common';
import { useSnackbar } from '../../../../hooks/useSnackbar';

interface SiteSettingsTabProps {
  editState: SiteEditState;
  updateEditState: (updates: Partial<SiteEditState>) => void;
  siteId: string | null;
  cachedImages?: ImageMetadata[];
  preloaded?: boolean;
  onSave?: (suppressNotification?: boolean) => Promise<void>;
  onPendingFaviconFile?: (file: File | null) => void;
}

export const SiteSettingsTab: React.FC<SiteSettingsTabProps> = ({ 
  editState, 
  updateEditState, 
  siteId, 
  cachedImages = [], 
  preloaded = false,
  onSave,
  onPendingFaviconFile,
}) => {
  const normalizeDomainInput = (value: string): string => {
    const trimmed = value.trim();
    if (!trimmed) {
      return '';
    }

    return trimmed
      .replace(/^[a-z][a-z0-9+.-]*:\/\//i, '')
      .split(/[/?#]/)[0]
      .replace(/:\d+$/, '')
      .replace(/\.+$/, '')
      .toLowerCase();
  };

  const { updateSite } = useSites();
  const [faviconLibraryModalOpen, setFaviconLibraryModalOpen] = useState(false);
  const [currentFaviconUrl, setCurrentFaviconUrl] = useState<string | null>(null);
  const [libraryImages, setLibraryImages] = useState<ImageMetadata[]>([]);
  const [libraryVideos, setLibraryVideos] = useState<ImageMetadata[]>([]);
  const [libraryImagesLoading, setLibraryImagesLoading] = useState(false);
  
  // メニューの状態
  const [faviconMenuAnchor, setFaviconMenuAnchor] = useState<HTMLElement | null>(null);
  
  // ファイル選択用のref
  const faviconFileInputRef = useRef<HTMLInputElement>(null);

  // ドメイン設定の状態
  const [domainConfig, setDomainConfig] = useState<DomainConfig | null>(null);
  const [domainLoading, setDomainLoading] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [basePath, setBasePath] = useState<string>('');
  const [embedMode, setEmbedMode] = useState<'direct' | 'embed'>('direct');
  const [showDeleteDomainDialog, setShowDeleteDomainDialog] = useState(false);
  
  // 会社名選択用の状態
  const [users, setUsers] = useState<User[]>([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [companySearchValue, setCompanySearchValue] = useState<string>('');
  const { snackbar, showSnackbar, handleClose: handleSnackbarClose } = useSnackbar();

  // ドメイン詳細モーダルの状態
  const [domainDetailOpen, setDomainDetailOpen] = useState(false);

  // ドメイン設定を読み込み
  useEffect(() => {
    if (siteId && siteId !== 'create') {
      loadDomainConfig();
    }
  }, [siteId]);

  // ユーザー一覧を読み込み
  useEffect(() => {
    loadUsers();
  }, []);

  // ライブラリ画像・動画を読み込み
  useEffect(() => {
    if (siteId && siteId !== 'create') {
      loadLibraryMedia();
    }
  }, [siteId, cachedImages, preloaded]);

  const loadLibraryMedia = async () => {
    if (!siteId || siteId === 'create') return;
    
    try {
      if (preloaded && cachedImages.length > 0) {
        const images = cachedImages.filter(img => !img.isVideo);
        const videos = cachedImages.filter(img => img.isVideo);
        setLibraryImages(images);
        setLibraryVideos(videos);
        setLibraryImagesLoading(false);
      } else {
        setLibraryImagesLoading(true);
        try {
          const allMedia = await ImageService.getSiteImageLibrary(siteId);
          const images = allMedia.filter(img => !img.isVideo);
          const videos = allMedia.filter(img => img.isVideo);
          setLibraryImages(images);
          setLibraryVideos(videos);
        } finally {
          setLibraryImagesLoading(false);
        }
      }
    } catch (error) {
      console.error('ライブラリの読み込みに失敗しました:', error);
      setLibraryImagesLoading(false);
    }
  };

  // 選択された会社名を設定
  useEffect(() => {
    if (editState.companyUserId && users.length > 0) {
      const selectedUser = users.find(u => u.id === editState.companyUserId);
      if (selectedUser) {
        setCompanySearchValue(selectedUser.displayName || '');
      }
    } else if (!editState.companyUserId) {
      setCompanySearchValue('');
    }
  }, [editState.companyUserId, users]);

  const loadUsers = async () => {
    try {
      setUsersLoading(true);
      const usersData = await UserService.getUsers();
      setUsers(usersData);
    } catch (error) {
      console.error('ユーザー一覧の取得に失敗しました:', error);
    } finally {
      setUsersLoading(false);
    }
  };

  const loadDomainConfig = async () => {
    if (!siteId || siteId === 'create') return;
    
    try {
      const config = await DomainService.getDomain(siteId);
      setDomainConfig(config);
      if (config) {
        updateEditState({ customDomain: config.domain });
        setBasePath(config.basePath || '');
        setEmbedMode(config.embedMode || 'direct');
      } else {
        // ドメイン設定が見つからない場合は空にする
        updateEditState({ customDomain: '' });
        setBasePath('');
        setEmbedMode('direct');
      }
    } catch (error) {
      console.error('ドメイン設定の読み込みエラー:', error);
      // エラー時も空にする
      updateEditState({ customDomain: '' });
      setBasePath('');
    }
  };

  // ファビコン選択時の処理
  const handleFaviconSelected = (imageUrl: string) => {
    if (updateEditState) {
      updateEditState({
        settings: {
          ...editState?.settings,
          favicon: imageUrl
        }
      });
    }
    setCurrentFaviconUrl(imageUrl);
  };

  // 使用中の画像URLを集約
  const getUsedImageUrls = (): Set<string> => {
    const usedUrls = new Set<string>();
    
    if (!editState) return usedUrls;
    
    // ページの背景画像
    if (editState.pages && Array.isArray(editState.pages)) {
      editState.pages.forEach((page: any) => {
        // ページの背景画像
        if (page.backgroundImage) {
          usedUrls.add(page.backgroundImage);
        }
        // ページスロットの画像
        if (page.slots && Array.isArray(page.slots)) {
          page.slots.forEach((slot: any) => {
            if (slot.imageUrl) {
              usedUrls.add(slot.imageUrl);
            }
            // スロット内のページオブジェクトの背景画像（もしあれば）
            if (slot.page && slot.page.backgroundImage) {
              usedUrls.add(slot.page.backgroundImage);
            }
          });
        }
        // ページの横スライド画像
        if (page.horizontalSlides && Array.isArray(page.horizontalSlides)) {
          page.horizontalSlides.forEach((slide: any) => {
            if (slide.backgroundImage) {
              usedUrls.add(slide.backgroundImage);
            }
            // 横スライド内のページオブジェクトの背景画像（もしあれば）
            if (slide.page && slide.page.backgroundImage) {
              usedUrls.add(slide.page.backgroundImage);
            }
          });
        }
      });
    }
    
    // CTAボタンの画像
    if (editState.settings && editState.settings.ctaButtons && Array.isArray(editState.settings.ctaButtons)) {
      editState.settings.ctaButtons.forEach((cta: any) => {
        if (cta.image) {
          usedUrls.add(cta.image);
        }
        if (cta.hoverImage) {
          usedUrls.add(cta.hoverImage);
        }
      });
    }
    
    // 設定のロゴ・ファビコン・背景画像
    if (editState.settings) {
      if (editState.settings.logo) {
        usedUrls.add(editState.settings.logo);
      }
      if (editState.settings.favicon) {
        usedUrls.add(editState.settings.favicon);
      }
      // ページ設定の背景画像（PCビュー）
      if (editState.settings.backgroundImage) {
        usedUrls.add(editState.settings.backgroundImage);
      }
    }
    
    return usedUrls;
  };

  // デバイスからファビコンをアップロード（新規時は一時保存のみ）
  const handleFaviconFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (!ImageService.isSupportedImageType(file) || !ImageService.isValidFileSize(file, 100)) {
      alert('画像ファイル（JPEG/PNG/WebP/GIF、最大100MB）を選択してください');
      return;
    }
    if (siteId === 'create' && onPendingFaviconFile) {
      onPendingFaviconFile(file);
      const url = URL.createObjectURL(file);
      handleFaviconSelected(url);
      if (faviconFileInputRef.current) faviconFileInputRef.current.value = '';
      return;
    }
    if (!siteId || siteId === 'create') return;
    try {
      let fileExtension = file.type === 'image/gif' ? 'gif' : 'webp';
      const originalName = file.name.replace(/\.[^/.]+$/, '');
      const originalFileName = `${originalName}.${fileExtension}`;
      const isVideo = false;
      const fileName = await ImageService.getAvailableFileName(originalFileName, siteId, isVideo);
      const uploadedUrl = await ImageService.uploadMedia(file, siteId, undefined, {
        fileName,
        overwrite: false,
        autoNumber: false,
      });
      
      handleFaviconSelected(uploadedUrl);
    } catch (error) {
      console.error('画像のアップロードに失敗しました:', error);
      alert('画像のアップロードに失敗しました');
    } finally {
      // ファイル入力をリセット
      if (faviconFileInputRef.current) {
        faviconFileInputRef.current.value = '';
      }
    }
  };

  // ドメイン設定ハンドラー
  const handleDomainSetup = async () => {
    if (!editState?.customDomain || !siteId || siteId === 'create') {
      showSnackbar('サイトを保存してからドメインを設定してください', 'error');
      return;
    }

    // 前後の空白を除去
    const domain = normalizeDomainInput(editState.customDomain);
    if (!domain) {
      showSnackbar('ドメインを入力してください', 'error');
      return;
    }
    if (domain !== editState.customDomain) {
      updateEditState({ customDomain: domain });
    }

    // 最小限のチェックのみ（厳密な形式はCloudflare・DNS側で判定されるため、有効なドメインを弾かないようにする）
    // - 空白・改行を含まない
    // - 少なくとも1つドットを含む（localhost/127.0.0.1 は埋め込み時のみ許可）
    // - 長さの上限のみ（DNSラベル63文字×数 + ドット等、255以内）
    const hasInvalidChars = /\s/.test(domain);
    const hasDot = domain.includes('.');
    const isLocalhost = embedMode === 'embed' && /^(localhost|127\.0\.0\.1)$/i.test(domain);
    const looksLikeDomain = hasDot || isLocalhost;
    if (hasInvalidChars || !looksLikeDomain || domain.length > 253) {
      const msg = hasInvalidChars
        ? 'ドメインに空白や改行は含めません'
        : !looksLikeDomain
          ? (embedMode === 'embed' ? 'ドメインを入力してください（例: aaa.net、localhost）' : 'ドメインを入力してください（例: example.com）')
          : 'ドメインが長すぎます';
      showSnackbar(msg, 'error');
      return;
    }

    // basePathの検証（直接方式のみ、空文字列または/で始まる形式）
    if (embedMode !== 'embed' && basePath && !basePath.match(/^\/[a-zA-Z0-9\/_-]*$/)) {
      showSnackbar('パスプレフィックスは/で始まる形式で入力してください（例: /campaign1）', 'error');
      return;
    }

    setDomainLoading(true);

    try {
      // 埋め込み式の場合はbasePathをundefinedにする
      const normalizedBasePath = embedMode === 'embed' ? undefined : (basePath.trim() || undefined);
      const config = await DomainService.createDomain(siteId, domain, normalizedBasePath, embedMode);
      setDomainConfig(config);
      
      // サイト情報を自動保存（通知を抑制して呼び出す）
      if (onSave) {
        await onSave(true); // suppressNotification = true
      }
      
      // ドメイン設定の通知のみ表示
      if (embedMode === 'embed') {
        showSnackbar('ドメイン設定が完了しました。埋め込み用のJSコードを確認してください。', 'success');
      } else {
        const pathInfo = normalizedBasePath ? ` (パス: ${normalizedBasePath})` : '';
        showSnackbar(`ドメイン設定が完了しました。ネームサーバーを設定してください。${pathInfo}`, 'success');
      }
    } catch (error: any) {
      showSnackbar(error.message || 'ドメイン設定に失敗しました', 'error');
    } finally {
      setDomainLoading(false);
    }
  };

  // ドメイン検証ハンドラー
  const handleVerifyDomain = async () => {
    if (!siteId || siteId === 'create') return;

    setVerifying(true);

    try {
      const config = await DomainService.verifyDomain(siteId);
      setDomainConfig(config);
      
      if (config.isActive) {
        const message = config.embedMode === 'embed' 
          ? 'ドメイン設定が完了しました！JSコードを埋め込んでください。'
          : 'ドメインが有効になりました！独自ドメインでアクセスできます。';
        showSnackbar(message, 'success');
      } else {
        showSnackbar('ステータスを更新しました。ネームサーバーの変更が反映されるまで最大48時間かかる場合があります。', 'info');
      }
    } catch (error: any) {
      showSnackbar(error.message || 'ドメイン検証に失敗しました', 'error');
    } finally {
      setVerifying(false);
    }
  };

  // ドメイン削除ハンドラー
  const handleDeleteDomain = () => {
    if (!siteId || siteId === 'create') return;
    setShowDeleteDomainDialog(true);
  };

  const confirmDeleteDomain = async () => {
    if (!siteId || siteId === 'create') return;

    setDomainLoading(true);

    try {
      // まずCloudflare上のZoneを削除
      await DomainService.deleteDomain(siteId);
      setDomainConfig(null);
      
      // サイト情報を自動保存（updateSiteを直接使用して画面遷移を避ける）
      // customDomainを空文字列に設定して保存
      if (siteId && siteId !== 'create') {
        try {
          // settingsからundefinedの値を除去
          const cleanSettings = editState.settings ? 
            Object.fromEntries(
              Object.entries(editState.settings).filter(([_, value]) => value !== undefined)
            ) : undefined;

          const siteData = {
            title: editState.title.trim(),
            description: editState.description || '',
            status: editState.status,
            pages: editState.pages || [],
            maxRows: editState.maxRows || 1,
            maxColumns: editState.maxColumns || 1,
            customDomain: '', // 空文字列に設定
            sslEnabled: editState.sslEnabled,
            ...(cleanSettings && Object.keys(cleanSettings).length > 0 && { settings: cleanSettings }),
          };
          
          await updateSite(siteId, siteData);
          
          // 保存成功後、editStateも更新
          updateEditState({ customDomain: '' });
        } catch (updateError: any) {
          console.error('サイト情報の更新エラー:', updateError);
          // 更新エラーが発生しても、editStateは更新する
          updateEditState({ customDomain: '' });
          // エラーを表示しない（Cloudflare削除は成功しているため）
        }
      } else {
        // siteIdが無効な場合でも、editStateは更新
        updateEditState({ customDomain: '' });
      }
      
      // 状態を再読み込みしてリセット
      await loadDomainConfig();
      
      showSnackbar('ドメイン設定を削除しました', 'success');
    } catch (error: any) {
      console.error('ドメイン削除エラー:', error);
      showSnackbar(error.message || 'ドメイン削除に失敗しました', 'error');
      // エラーが発生しても、editStateは更新する
      updateEditState({ customDomain: '' });
    } finally {
      setDomainLoading(false);
      setShowDeleteDomainDialog(false);
    }
  };


  // ネームサーバーをコピー
  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    showSnackbar('クリップボードにコピーしました', 'success');
  };

  return (
    <Box sx={{ p: 2, width: '800px' }}>
      <ConfirmDialog
        open={showDeleteDomainDialog}
        onClose={() => setShowDeleteDomainDialog(false)}
        onConfirm={confirmDeleteDomain}
        message="削除します。よろしいですか？"
        confirmText="削除"
        confirmColor="#DB2323"
        confirmHoverColor="#C01F1F"
      />
      {/* 画像選択ダイアログ ※保存済みサイトのみ */}
      {siteId && siteId !== 'create' && (
        <>
          <ImageLibraryModal
            open={faviconLibraryModalOpen}
            onClose={() => setFaviconLibraryModalOpen(false)}
            onSelect={handleFaviconSelected}
            images={libraryImages}
            videos={libraryVideos}
            title="ファビコンを選択"
            usedImageUrls={getUsedImageUrls()}
            loading={libraryImagesLoading}
          />
        </>
      )}
      
      {/* ロゴ・ファビコン選択メニュー（新規時も表示） */}
      {siteId && (
        <>
          <Menu
            anchorEl={faviconMenuAnchor}
            open={Boolean(faviconMenuAnchor)}
            onClose={() => setFaviconMenuAnchor(null)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            transformOrigin={{ vertical: 'top', horizontal: 'left' }}
            PaperProps={{ sx: { borderRadius: '8px', boxShadow: '0px 4px 12px rgba(0,0,0,0.15)', mt: 0.5, minWidth: 180 } }}
          >
            <MenuItem onClick={() => { setFaviconMenuAnchor(null); faviconFileInputRef.current?.click(); }} sx={{ py: 1.5, px: 2 }}>
              <Box sx={{ mr: 1.5, display: 'flex', alignItems: 'center' }}><UploadIcon sx={{ fontSize: '18px', color: '#616161' }} /></Box>
              デバイスから選択
            </MenuItem>
            <MenuItem onClick={() => { setFaviconMenuAnchor(null); if (siteId !== 'create') setFaviconLibraryModalOpen(true); }} sx={{ py: 1.5, px: 2 }}>
              <Box sx={{ mr: 1.5, display: 'flex', alignItems: 'center' }}><LibraryIcon sx={{ fontSize: '18px', color: '#616161' }} /></Box>
              ライブラリから選択
            </MenuItem>
          </Menu>
          <input ref={faviconFileInputRef} type="file" accept="image/jpeg,image/jpg,image/png,image/webp,image/gif" onChange={handleFaviconFileSelect} style={{ display: 'none' }} />
        </>
      )}
      
      {/* 基本情報セクション */}
      <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600, mb: 2 }}>
        基本情報
      </Typography>
      <TextField
        fullWidth
        label="サイト名"
        placeholder="株式会社 ザオリク"
        variant="outlined"
        value={editState.title}
        onChange={(e) => updateEditState({ title: e.target.value })}
        sx={{ 
          mb: 2,
          '& .MuiOutlinedInput-root': {
            borderRadius: '12px',
            backgroundColor: '#FFFFFF',
          }
        }}
      />
      <Autocomplete
        options={users}
        getOptionLabel={(option) => option.displayName || option.email || ''}
        loading={usersLoading}
        value={users.find(u => u.id === editState.companyUserId) || null}
        onChange={(event, newValue) => {
          updateEditState({ companyUserId: newValue?.id || undefined });
        }}
        inputValue={companySearchValue}
        onInputChange={(event, newInputValue) => {
          setCompanySearchValue(newInputValue);
        }}
        filterOptions={(options, { inputValue }) => {
          return options.filter(option => {
            const displayName = option.displayName || '';
            const email = option.email || '';
            const searchLower = inputValue.toLowerCase();
            return displayName.toLowerCase().includes(searchLower) || 
                   email.toLowerCase().includes(searchLower);
          });
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            label="クライアント名"
            placeholder="クライアント名を入力"
            variant="outlined"
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: '12px',
              backgroundColor: '#FFFFFF',
              overflow: 'hidden',
            }
          }}
          />
        )}
        sx={{ mb: 2 }}
      />
      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Box
            onClick={() => {
              updateEditState({
                settings: {
                  ...editState.settings,
                  showPageCount: !(editState.settings?.showPageCount !== false)
                }
              });
            }}
            sx={{
              position: 'relative',
              width: '48px',
              height: '24px',
              backgroundColor: editState.settings?.showPageCount !== false ? '#1B76D2' : '#E0E0E0',
              borderRadius: '12px',
              cursor: 'pointer',
              transition: 'background-color 0.3s',
              display: 'flex',
              alignItems: 'center',
              padding: '2px',
            }}
          >
            <Box
              sx={{
                width: '20px',
                height: '20px',
                backgroundColor: '#FFFFFF',
                borderRadius: '50%',
                transform: editState.settings?.showPageCount !== false ? 'translateX(24px)' : 'translateX(0px)',
                transition: 'transform 0.3s',
                boxShadow: '0 2px 4px rgba(0, 0, 0, 0.2)',
              }}
            />
          </Box>
          <Typography variant="body2" sx={{ fontSize: '14px', color: '#000000' }}>
            ページカウント表示: {editState.settings?.showPageCount !== false ? 'ON' : 'OFF'}
          </Typography>
        </Box>
      </Box>

      {/* ロゴ・ファビコンセクション */}
      <Box sx={{ width: { xs: '100%', sm: 'calc(50% - 12px)' }, mb: 3 }}>
        <ImageUploadField
          label="ファビコン"
          helperText="JPEG, PNG, WebP, GIF (最大100MB)"
          value={editState?.settings?.favicon}
          onClick={(e) => {
            if (siteId) {
              setFaviconMenuAnchor(e.currentTarget);
            }
          }}
          onDelete={() => {
            updateEditState({
              settings: {
                ...editState?.settings,
                favicon: undefined
              }
            });
          }}
          alt="ファビコン"
        />
      </Box>

      {/* ドメイン設定セクション */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
          ドメイン設定
        </Typography>
        {domainConfig?.isActive && (
          <Box 
            sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: 0.5,
              cursor: 'pointer',
              '&:hover': {
                opacity: 0.8
              }
            }}
            onClick={() => setDomainDetailOpen(true)}
          >
            <Box sx={{ 
              width: 20, 
              height: 20, 
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}>
              <img 
                src="/assets/images/check_green.svg" 
                alt="有効" 
                style={{ width: '20px', height: '20px' }}
              />
            </Box>
            <Typography variant="body2" sx={{ color: '#4CAF50', fontWeight: 500 }}>
              有効
            </Typography>
            <ChevronRightIcon sx={{ fontSize: 20, color: '#666666' }} />
          </Box>
        )}
      </Box>

      {/* 設定方式の選択 */}
      <FormControl fullWidth sx={{ mb: 3 }}>
        <InputLabel id="embed-mode-select-label" sx={{ '&.Mui-disabled': { color: '#000000' } }}>設定方式</InputLabel>
        <Select
          labelId="embed-mode-select-label"
          id="embed-mode-select"
          value={embedMode}
          label="設定方式"
          onChange={(e) => setEmbedMode(e.target.value as 'direct' | 'embed')}
          disabled={!!domainConfig}
          sx={{
            borderRadius: '12px',
            backgroundColor: '#FFFFFF',
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: '#D0D0D0',
            },
            '&.Mui-disabled': {
              backgroundColor: '#EBEBEB',
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: 'transparent',
              },
              '& .MuiSelect-select': {
                color: '#000000',
                WebkitTextFillColor: '#000000',
              },
            },
          }}
        >
          <MenuItem value="direct">直接方式（ドメイン全体またはパスで表示）</MenuItem>
          <MenuItem value="embed">埋め込み方式（既存サーバーにJSを埋め込む）</MenuItem>
        </Select>
      </FormControl>

      <TextField
        fullWidth
        placeholder="example.com"
        variant="outlined"
        value={editState.customDomain}
        onChange={(e) => updateEditState({ customDomain: e.target.value })}
        onBlur={(e) => {
          const normalized = normalizeDomainInput(e.target.value);
          if (normalized !== e.target.value) {
            updateEditState({ customDomain: normalized });
          }
        }}
        helperText={
          embedMode === 'embed'
            ? 'JSを埋め込む既存サーバーのドメインを入力してください（例: aaa.jp）。このドメイン以外では埋め込みサイトは表示されません。https:// は付けず、ドメイン名のみ入力してください。'
            : '独自ドメインを入力してください（例: example.com）。URLを貼り付けた場合はフォーカスを外すとドメインのみに整えます。https:// は付けず、ドメイン名のみ入力してください。'
        }
        disabled={!!domainConfig}
        sx={{ 
          mb: 2,
          '& .MuiOutlinedInput-root': {
            borderRadius: '12px',
            backgroundColor: '#FFFFFF',
            '& fieldset': {
              borderColor: '#D0D0D0',
            },
            '& .MuiInputBase-input': {
              color: '#000000',
            },
            '&.Mui-disabled': {
              backgroundColor: '#EBEBEB',
              '& fieldset': {
                borderColor: 'transparent',
              },
              '& .MuiInputBase-input': {
                color: '#000000',
              },
            },
          },
          '& .MuiInputLabel-root.Mui-disabled': {
            color: '#000000',
          },
        }}
        InputProps={{
          inputProps: {
            style: {
              WebkitTextFillColor: '#000000',
              color: '#000000',
            },
          },
        }}
      />

      {/* パスプレフィックス入力（直接方式のみ） */}
      {embedMode !== 'embed' && (
        <TextField
          fullWidth
          label={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Typography component="span" variant="body2" sx={{ color: '#999999' }}>*任意</Typography>
              <Typography component="span" variant="body2">パスプレフィックス (例: /campaign1)</Typography>
            </Box>
          }
          placeholder="/campaign1"
          variant="outlined"
          value={basePath}
          onChange={(e) => setBasePath(e.target.value)}
          disabled={!!domainConfig}
          sx={{ 
            mb: 2,
            '& .MuiOutlinedInput-root': {
              borderRadius: '12px',
              backgroundColor: '#FFFFFF',
              '& fieldset': {
                borderColor: '#D0D0D0',
              },
              '& .MuiInputBase-input': {
                color: '#000000',
              },
              '&.Mui-disabled': {
                backgroundColor: '#EBEBEB',
                '& fieldset': {
                  borderColor: 'transparent',
                },
                '& .MuiInputBase-input': {
                  color: '#000000',
                },
              },
            },
            '& .MuiInputLabel-root.Mui-disabled': {
              color: '#000000',
            },
          }}
          InputProps={{
            inputProps: {
              style: {
                WebkitTextFillColor: '#000000',
                color: '#000000',
              },
            },
          }}
        />
      )}

      {/* ドメイン設定ボタン */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
        {!domainConfig && (
        <Button
          variant="contained"
            startIcon={domainLoading ? <CircularProgress size={18} color="inherit" /> : <DomainIcon />}
          onClick={handleDomainSetup}
            disabled={!editState.customDomain || !siteId || siteId === 'create' || domainLoading}
        >
            {domainLoading ? (embedMode === 'embed' ? '設定中...' : 'Zone作成中...') : 'ドメインを設定'}
          </Button>
        )}
      </Box>

      {/* ドメイン詳細モーダル */}
      <RightDrawer
        open={domainDetailOpen}
        onClose={() => setDomainDetailOpen(false)}
        width={{ xs: '100%', sm: '500px' }}
      >
        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
          {/* ヘッダー */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 3, borderBottom: '1px solid #E0E0E0' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <IconButton
                size="small"
                onClick={() => setDomainDetailOpen(false)}
                sx={{ 
                  padding: '8px',
                }}
              >
                <ChevronLeftIcon />
              </IconButton>
              <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '18px' }}>
                ドメイン設定 詳細
              </Typography>
            </Box>
            <IconButton
              size="small"
              onClick={() => setDomainDetailOpen(false)}
              sx={{
                padding: '8px',
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>

          {/* コンテンツ */}
          {domainConfig && (
            <Box sx={{ flex: 1, overflow: 'auto', p: 3 }}>
              <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600, mb: 2, fontSize: '16px' }}>
                {domainConfig.embedMode === 'embed' ? '埋め込み用JSコード' : 'ドメイン情報'}
              </Typography>

              {domainConfig.embedMode === 'embed' ? (
                <>
                  <Typography variant="body2" sx={{ mb: 2, fontSize: '14px', color: '#000000' }}>
                    既存サーバーのページ(https://exsample.com/campaign1)に以下のコードを埋め込んでください。
                  </Typography>
                  <Box sx={{ mb: 3 }}>
                    <Box sx={{ 
                      backgroundColor: '#FFFFFF', 
                      border: '1px solid #00000033', 
                      borderRadius: '8px', 
                      p: 2,
                      display: 'flex',
                      gap: 2,
                      alignItems: 'flex-start'
                    }}>
                      <Box sx={{ 
                        flex: 1,
                        overflow: 'auto',
                        maxHeight: '200px'
                      }}>
                      <Typography 
                        component="pre" 
                        sx={{ 
                          fontSize: '12px', 
                          fontFamily: 'monospace',
                          whiteSpace: 'pre-wrap',
                          wordBreak: 'break-all',
                          margin: 0,
                          color: '#000000',
                        }}
                      >
{`<!-- Qwipe埋め込み用のコンテナ -->
<div id="qwipe-container"></div>

<!-- Qwipe埋め込みスクリプト -->
<script>
    window.qwipeEmbedConfig = {
        siteId: '${siteId}',
        containerId: 'qwipe-container'
    };
</script>
<script async src="https://qwipe.jp/embed.js"></script>`}
                      </Typography>
                      </Box>
                      <IconButton
                        size="small"
                        onClick={() => {
                          const embedCode = `<!-- Qwipe埋め込み用のコンテナ -->
<div id="qwipe-container"></div>

<!-- Qwipe埋め込みスクリプト -->
<script>
    window.qwipeEmbedConfig = {
        siteId: '${siteId}',
        containerId: 'qwipe-container'
    };
</script>
<script async src="https://qwipe.jp/embed.js"></script>`;
                          navigator.clipboard.writeText(embedCode).then(() => {
                            showSnackbar('JavaScriptコードをクリップボードにコピーしました', 'success');
                          });
                        }}
                        sx={{
                          backgroundColor: '#9E9E9E',
                          color: '#FFFFFF',
                          width: 40,
                          height: 40,
                          padding: 0,
                          borderRadius: '8px',
                          '&:hover': {
                            backgroundColor: '#757575',
                          },
                        }}
                      >
                        <ContentCopyIcon sx={{ fontSize: 20 }} />
                      </IconButton>
                    </Box>
                  </Box>
                </>
              ) : domainConfig.isActive ? (
                <>
                  <Typography variant="body2" sx={{ mb: 2, fontSize: '14px', color: '#000000' }}>
                    ドメインが有効です。以下のURLでアクセスできます。
                  </Typography>
                  <Box sx={{ mb: 3 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography 
                        variant="body1" 
                        sx={{ 
                          fontWeight: 400,
                          color: '#1B76D2',
                          fontSize: '14px',
                          textDecoration: 'none',
                          '&:hover': {
                            textDecoration: 'underline'
                          }
                        }}
                        component="a"
                        href={`https://${domainConfig.domain}${domainConfig.basePath || '/'}`}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        https://{domainConfig.domain}{domainConfig.basePath || '/'}
                      </Typography>
                    </Box>
                  </Box>
                </>
              ) : null}

              {domainConfig.embedMode !== 'embed' && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="body2" color="text.secondary" display="block" sx={{ mb: 1, fontSize: '11px' }}>
                    Zone ID: {domainConfig.zoneId}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" display="block" sx={{ mb: 1, fontSize: '11px' }}>
                    パスプレフィックス: {domainConfig.basePath || '/'}
                  </Typography>
                  {domainConfig.createdAt && (
                    <Typography variant="body2" color="text.secondary" display="block" sx={{ fontSize: '11px' }}>
                      作成日: {new Date(domainConfig.createdAt).toLocaleString('ja-JP')}
                    </Typography>
                  )}
                </Box>
              )}
              
              {domainConfig.embedMode === 'embed' && (
                <Box sx={{ mb: 3 }}>
                  {domainConfig.createdAt && (
                    <Typography variant="body2" color="text.secondary" display="block" sx={{ fontSize: '11px' }}>
                      作成日: {new Date(domainConfig.createdAt).toLocaleString('ja-JP')}
                    </Typography>
                  )}
                </Box>
              )}

              <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                  <IconButton
                  onClick={handleDeleteDomain}
                  disabled={domainLoading}
                    sx={{
                    backgroundColor: '#FFFFFF',
                    border: '1px dashed #F44336',
                      width: 40,
                      height: 40,
                      padding: 0,
                      borderRadius: '8px',
                      '&:hover': {
                      backgroundColor: '#FFFFFF',
                      },
                    }}
                  >
                  <img 
                    src="/assets/images/trash.svg" 
                    alt="削除" 
                    style={{ width: '20px', height: '20px', filter: 'none' }}
                  />
                </IconButton>
              </Box>
            </Box>
          )}
        </Box>
      </RightDrawer>


      {/* スナックバー - 右からスライドイン */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        TransitionComponent={(props) => <Slide {...props} direction="left" />}
      >
        <Alert 
          onClose={handleSnackbarClose} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};
